// Tiny custom emoji picker — ~80 common emojis, no dependency.
//
// Why not emoji-mart / emoji-picker-react: those bundles ship 1-2MB
// of emoji metadata + sprite sheets for a feature most users hit
// occasionally in chat. A handful of common emojis covers 95% of
// the actual use, fits in <2kB of code, and renders instantly. If
// agents start asking for skin-tone variants or search, swap in a
// real library at that point.
//
// Used by:
//   * CustomerAssistWidget (agent chat compose) — anchors upward
//   * routes/assist.tsx (client chat compose) — anchors upward
//   * RichBodyToolbar (reply + compose) — anchors downward (sits at the
//     top of the editor; opening upward would clip out of the dialog)
//   * NotesPanel (team-note composer) — anchors upward
// Same shape for all: a small popover anchored to a trigger button.
// `onPick(emoji)` is called with the raw emoji character; the parent
// is responsible for inserting it at the textarea / contentEditable caret.
//
// ─── Portaling note ─────────────────────────────────────────────────────
// The popover renders into document.body via createPortal so it can NEVER
// be clipped by an ancestor with `overflow: hidden` (e.g. the Notes panel
// card, the reply composer's bordered body, a sticky scrolling rail).
// The popover uses `position: fixed` + the trigger's
// getBoundingClientRect() to anchor itself; a ResizeObserver + scroll +
// resize listeners keep it pinned to the trigger while open.

import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { Smile } from 'lucide-react';
import { cn } from '@/lib/utils';

const EMOJI_GROUPS: { label: string; emojis: string[] }[] = [
  {
    label: 'Faces',
    emojis: [
      '😀', '😃', '😄', '😁', '😊', '🙂', '😉', '😍',
      '😘', '😎', '🤩', '🥳', '😇', '🙃', '😅', '😂',
      '🤣', '😭', '😢', '😬', '😳', '😤', '😡', '🤔',
      '😴', '🥱', '🤯', '😱',
    ],
  },
  {
    label: 'Gestures',
    emojis: [
      '👍', '👎', '👏', '🙌', '🙏', '👌', '✌️', '🤝',
      '🤞', '💪', '👋', '🤙', '✊', '🤘',
    ],
  },
  {
    label: 'Hearts & symbols',
    emojis: [
      '❤️', '🧡', '💛', '💚', '💙', '💜', '🖤', '🤍',
      '💯', '💥', '⭐', '✨', '🌟', '🔥', '🎉', '🎊',
      '✅', '❌', '⚠️', '❓', '❗', '💡', '📌', '🔗',
    ],
  },
];

// Width of the popover in CSS px. Matches Tailwind w-72 (288px).
const POPOVER_WIDTH = 288;
// Approx. height — used for the upward-anchored variant to make sure
// the popover sits above the trigger by its actual height. The internal
// scroll container is max-h-72 + padding/header, so ~310px is close
// enough; the popover renders flush-to-trigger and we let any overflow
// at the top edge get scrolled by the inner panel.
const POPOVER_HEIGHT_APPROX = 310;
// Gap between the trigger and the popover.
const GAP = 6;
// Safe inset from the viewport edges so the popover never hugs them.
const VIEWPORT_INSET = 8;

interface Props {
  /** Element the picker anchors to. The picker renders a popover
   *  immediately above-right (default) or below-right of this trigger. */
  trigger: ReactNode;
  /** Called with the picked emoji character. The parent inserts it
   *  at the textarea caret (or wherever makes sense). */
  onPick: (emoji: string) => void;
  /** Which side of the trigger the popover anchors to. `top` opens
   *  upward (default — right for compose surfaces at the bottom of a
   *  container). `bottom` opens downward — right when the trigger
   *  lives at the TOP of the surface (e.g. a formatting toolbar). */
  side?: 'top' | 'bottom';
  /** Horizontal alignment of the popover relative to the trigger.
   *  `right` (default) right-aligns to the trigger so the popover
   *  doesn't poke off the right edge of a tight container. `left`
   *  left-aligns — useful when the trigger sits at the left of a
   *  toolbar with room to its right. */
  align?: 'left' | 'right';
  /** Optional className on the wrapper — defaults to inline-flex so
   *  the trigger sits naturally in a button row. */
  className?: string;
  /** Controlled open state (optional). When provided the parent owns
   *  open/close — used to open the picker from a keyboard shortcut. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function EmojiPicker({ trigger, onPick, side = 'top', align = 'right', className, open: openProp, onOpenChange }: Props) {
  const [internalOpen, setInternalOpen] = useState(false);
  // Controlled when `openProp` is provided; otherwise self-managed.
  const open = openProp ?? internalOpen;
  function setOpen(next: boolean) {
    onOpenChange?.(next);
    if (openProp === undefined) setInternalOpen(next);
  }
  // Span around the trigger we measure against for anchoring.
  const triggerRef = useRef<HTMLSpanElement | null>(null);
  // The portaled popover root — used for click-outside detection so
  // clicking INSIDE the popover doesn't dismiss it (it lives outside
  // triggerRef now).
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);

  // Recompute popover coords from the trigger's current bounding rect.
  // Called on open + on every scroll/resize tick while open.
  function reposition() {
    const tr = triggerRef.current?.getBoundingClientRect();
    if (!tr) return;

    // Vertical: above (side='top') or below (side='bottom') the trigger.
    // Flip automatically if there isn't enough room on the preferred side.
    let top = side === 'top'
      ? tr.top - POPOVER_HEIGHT_APPROX - GAP
      : tr.bottom + GAP;
    // If the preferred side overflows the viewport, flip to the other side.
    if (side === 'top' && top < VIEWPORT_INSET) {
      top = tr.bottom + GAP;
    } else if (
      side === 'bottom' &&
      top + POPOVER_HEIGHT_APPROX > window.innerHeight - VIEWPORT_INSET
    ) {
      top = tr.top - POPOVER_HEIGHT_APPROX - GAP;
    }
    // Clamp inside the viewport as a last resort (very small phones).
    top = Math.max(
      VIEWPORT_INSET,
      Math.min(top, window.innerHeight - POPOVER_HEIGHT_APPROX - VIEWPORT_INSET),
    );

    // Horizontal: right-aligned (default) anchors the popover's right
    // edge to the trigger's right edge; left-aligned does the inverse.
    let left = align === 'right'
      ? tr.right - POPOVER_WIDTH
      : tr.left;
    // Clamp to viewport.
    left = Math.max(
      VIEWPORT_INSET,
      Math.min(left, window.innerWidth - POPOVER_WIDTH - VIEWPORT_INSET),
    );

    setCoords({ top, left });
  }

  // Measure on open + whenever the trigger moves. useLayoutEffect so
  // the initial paint already has the right coords (no flash at 0,0).
  useLayoutEffect(() => {
    if (!open) return;
    reposition();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, side, align]);

  // Re-pin on scroll + resize while open. Passive listener so we never
  // block scrolling. `capture: true` catches scrolls on inner overflow
  // containers (e.g. the notes panel rail) too, not just on window.
  useEffect(() => {
    if (!open) return;
    const onScrollOrResize = () => reposition();
    window.addEventListener('scroll', onScrollOrResize, { passive: true, capture: true });
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      window.removeEventListener('scroll', onScrollOrResize, { capture: true });
      window.removeEventListener('resize', onScrollOrResize);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Click-outside closes the popover. Now that the popover lives in a
  // portal, we have to check BOTH the trigger AND the popover element
  // — clicking inside either is still "inside" the picker.
  useEffect(() => {
    if (!open) return;
    function onDocClick(ev: MouseEvent) {
      const t = triggerRef.current;
      const p = popoverRef.current;
      if (!(ev.target instanceof Node)) return;
      if (t?.contains(ev.target)) return;
      if (p?.contains(ev.target)) return;
      setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  // Escape closes too — matches user expectation for any popover.
  useEffect(() => {
    if (!open) return;
    function onKey(ev: KeyboardEvent) {
      if (ev.key === 'Escape') setOpen(false);
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  return (
    <span ref={triggerRef} className={cn('relative inline-flex', className)}>
      <span onClick={() => setOpen(!open)}>{trigger}</span>
      {open && coords
        ? createPortal(
            <div
              ref={popoverRef}
              // mousedown.preventDefault keeps the textarea / contentEditable
              // caret stable when the agent clicks an emoji; without it the
              // editor blurs and `document.execCommand('insertText')` in the
              // parent runs against a missing selection.
              onMouseDown={(e) => e.preventDefault()}
              style={{ position: 'fixed', top: coords.top, left: coords.left, width: POPOVER_WIDTH }}
              className={cn(
                // z-[60] sits above Radix Dialog overlay/content (z-50).
                // pointer-events-auto is ESSENTIAL: a modal Radix Dialog sets
                // `pointer-events:none` on <body>, and this popover is portaled
                // to <body> OUTSIDE the dialog content — without this it renders
                // but every emoji is unclickable. Re-enabling it here fixes the
                // emoji picker inside ComposeDialog and other modal composers.
                'pointer-events-auto z-[60] max-w-[calc(100vw-1rem)] overflow-hidden rounded-lg border border-border bg-background shadow-xl',
                // Subtle fade-in — matches the rest of the app's
                // tw-animate-css usage on dialogs / toasts.
                'animate-in fade-in-0 zoom-in-95',
              )}
            >
              <div className="max-h-72 overflow-y-auto p-2">
                {EMOJI_GROUPS.map((group) => (
                  <div key={group.label} className="mb-2 last:mb-0">
                    <div className="mb-1 text-3xs font-medium uppercase tracking-wide text-muted-foreground">
                      {group.label}
                    </div>
                    <div className="grid grid-cols-8 gap-0.5">
                      {group.emojis.map((emoji) => (
                        <button
                          key={emoji}
                          type="button"
                          onClick={() => {
                            onPick(emoji);
                            setOpen(false);
                          }}
                          className="flex h-7 w-7 items-center justify-center rounded text-base transition-colors hover:bg-accent"
                          title={emoji}
                        >
                          {emoji}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>,
            document.body,
          )
        : null}
    </span>
  );
}

/** Convenience trigger — used by every composer so they don't each
 *  re-style a Smile icon button. Defaults match the assist surfaces
 *  (h-8, upward-opening, right-aligned); the toolbar variants pass
 *  `size="sm"` + `side="bottom"` so the button sits neatly in a row
 *  of formatting glyphs and the popover opens away from the dialog
 *  top edge. */
export function EmojiPickerButton({
  onPick,
  disabled,
  size = 'md',
  side,
  align,
  title = 'Insert an emoji',
  open,
  onOpenChange,
}: {
  onPick: (emoji: string) => void;
  disabled?: boolean;
  /** `sm` (h-7 w-7) matches the RichBodyToolbar / NotesPanel action
   *  rows. `md` (h-8 w-8 — default) matches the assist chat composers. */
  size?: 'sm' | 'md';
  side?: 'top' | 'bottom';
  align?: 'left' | 'right';
  /** Tooltip / aria-label override. */
  title?: string;
  /** Optional controlled open (e.g. a keyboard shortcut opening the picker). */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}) {
  const sizeCls = size === 'sm' ? 'h-7 w-7' : 'h-8 w-8';
  const iconCls = size === 'sm' ? 'h-3.5 w-3.5' : 'h-4 w-4';
  return (
    <EmojiPicker
      onPick={onPick}
      side={side}
      align={align}
      open={open}
      onOpenChange={onOpenChange}
      trigger={
        <button
          type="button"
          disabled={disabled}
          title={title}
          aria-label={title}
          className={cn(
            'inline-flex cursor-pointer items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50',
            sizeCls,
          )}
        >
          <Smile className={iconCls} />
        </button>
      }
    />
  );
}
